'use client'

import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  ArrowLeft,
  ChevronRight,
  FileText,
  LogIn,
  Menu,
  Search,
  User,
  X,
} from 'lucide-react'
import slugify from 'slugify'

import { useSession, useTenant } from '@rewardops/auth'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
  LocalizedLink,
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  useFavouritingAndWishlistingStore,
  useFilter,
  useLocale,
} from '@rewardops/ui'
import { isValidUrl } from '@rewardops/utils'

import { filterShopCategories } from '~/utils/category-utils'
import { filterNavLinks } from '~/utils/filter-utils'
import { formatTitleToId } from '~/utils/id-format-utils'
import { getSearchDropdownOptions, SearchTypes } from '~/utils/sort-utils'
import { replacePlaceholders } from '~/utils/translation-utils'
import { BrandedCategoryLinks } from '~/components/branded-category-links/branded-category-links'
import { useIsClient } from '~/components/hooks/use-is-client'
import { useLocaleChange } from '~/components/hooks/use-locale-change'
import { useNavigationLinks } from '~/components/hooks/use-navigation-links'
import { useSearchBox } from '~/components/hooks/use-search-box'
import MapleLogo from '~/components/maple/maple-logo'
import { PointsBalance } from '~/components/maple/navbar/points-balance'
import { styling } from '~/components/styling'
import { useTranslation } from '~/app/i18n/client'
import { useCart } from '~/context/cart-context'
import { ElpPromotionsContext } from '~/context/elp-promotions-context'
import { PromotionsContext } from '~/context/promotions-context'
import { ResourcesContext } from '~/context/resources-provider'
import { MapleSelect } from '../components/select'
import { HeartLink, ShoppingCartLink } from './link-icons'
import { MobileMenuLink } from './mobile-menu-link'
import { SearchBar } from './search-bar'
import { SearchOverlay } from './search-overlay'
import { UserInitial } from './user-initial'

type MobileNavLinkPropType = {
  id: string
  path: string
  icon: React.ReactNode
  translation: string
  isClient?: boolean
  disabled?: boolean
  onClick?: () => void
  target?: string
}

const Item = ({
  children,
  ...props
}: {
  children: React.ReactNode
  [key: string]: any
}) => (
  <div className="mx-4 border-b px-2 py-4" {...props}>
    {children}
  </div>
)

export function MobileMainNav({
  categories,
  isLoading,
}: {
  categories: any
  isLoading: boolean
}) {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const [isMainNavOpen, setIsMainNavOpen] = React.useState(false)
  const [isShopCategoriesOpen, setIsShopCategoriesOpen] = React.useState(false)
  const [menuLinkOpen, setMenuLinkOpen] = useState(0)
  const { getCartQuantity } = useCart()
  const tenant = useTenant()
  const { locale } = useLocale()
  const resources = useContext(ResourcesContext)

  const { searchQuery, searchType } = useFilter()

  const { promotionsLoading, totalItems } = useContext(PromotionsContext)
  const { showPromotionsLink } = useContext(ElpPromotionsContext)

  const {
    isSearchBarOpened,
    openSearchBar,
    debouncedState,
    updateSearchQuery,
    resetSearch,
  } = useSearchBox()

  const searchOverlay = useMemo(() => {
    if (!searchQuery || !searchType) return null
    return <SearchOverlay />
  }, [searchQuery, searchType])

  const retailersEnabled = tenant?.flags?.retailers

  const cartQuantity = getCartQuantity()

  const { savedItemIds } = useFavouritingAndWishlistingStore((state) => state)

  const isStackedDesktopNavbar = tenant?.stackedNavBarLinks?.[0]

  const [authorizeUrl, setAuthorizeUrl] = useState('')

  useEffect(() => {
    if (tenant?.theme === 'pointsAtWork' && tenant?.flags?.loginPage) {
      setAuthorizeUrl('/login')
    } else {
      fetch(
        `/api/auth/login?returnUrl=${encodeURIComponent(
          window.location.href,
        )}&locale=${locale}`,
      )
        .then((response) => response.json())
        .then((response) => {
          setAuthorizeUrl(response?.authRedirect?.url)
        })
    }
  }, [tenant?.theme, locale])

  const showSpecialOffers =
    !promotionsLoading && totalItems > 0 && tenant?.flags.specialOffersPage

  const isShopAndEarnEnabled =
    !resources?.navigationMenu?.omitLinks?.includes('retailers') &&
    !resources?.isNavigationMenuLoading

  const isSpecialOffersEnabled =
    !resources?.navigationMenu?.omitLinks?.includes('special-offers') &&
    !resources?.isNavigationMenuLoading

  const { directLinks, menuLinks } = useNavigationLinks({
    direct: resources?.navigationMenu?.directLinks,
    menu: resources?.navigationMenu?.menuLinks,
  })

  return (
    <header
      className={`sticky top-0 z-40 border-b ${
        isStackedDesktopNavbar
          ? 'bg-tenant-primary-color'
          : 'bg-nav-color text-nav-text-color'
      }`}
    >
      <nav className="z-50">
        <Sheet open={isMainNavOpen} onOpenChange={setIsMainNavOpen}>
          {isSearchBarOpened ? (
            <div className="flex justify-between">
              <div className="relative w-full">
                <SearchBar
                  selector={
                    <MapleSelect
                      id={`search-type-selector`}
                      className="text-grey-300 w-auto border-l bg-gray-100 p-2 text-sm"
                      selectItemClassName="font-normal text-grey-300"
                      placeholder="Select search type"
                      defaultValue={searchType || SearchTypes.Products}
                      options={getSearchDropdownOptions(t)}
                      onValueChange={(option) =>
                        updateSearchQuery({ query: searchQuery, qType: option })
                      }
                      withBorder={true}
                    />
                  }
                  onChange={updateSearchQuery}
                  onClose={() => resetSearch()}
                  searchQuery={debouncedState.query}
                  selectorDisabled={!retailersEnabled}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-1">
              <SheetTrigger asChild>
                <button id="mobile-main-menu" aria-label={t('nav.menu')}>
                  <Menu
                    className={`h-6 w-6 text-nav-text-color hover:text-tenant-primary-color ${
                      isStackedDesktopNavbar && 'text-white hover:text-white'
                    }`}
                    role="img"
                  >
                    <title>{t('nav.menu')}</title>
                  </Menu>
                </button>
              </SheetTrigger>
              <MapleLogo />
              <div className="inline-flex gap-2">
                {tenant?.flags?.searchEnabled && (
                  <button
                    id="search"
                    className="hover:text-tenant-primary-color"
                    aria-label={t('nav.search')}
                    onClick={() => openSearchBar()}
                  >
                    <Search
                      id="search"
                      color={`${
                        isStackedDesktopNavbar ? 'white' : 'currentColor'
                      }`}
                    />
                  </button>
                )}
                {tenant?.flags?.shoppingCart && (
                  <ShoppingCartLink
                    cartQuantity={cartQuantity}
                    className={`${
                      isStackedDesktopNavbar
                        ? 'text-white hover:text-white'
                        : 'text-nav-text-color'
                    }`}
                  />
                )}
              </div>
            </div>
          )}
          <SheetContent
            side="left"
            className="h-full w-full max-w-none p-0"
            closeIcon={
              <X
                id="close-mobile-main-menu"
                className="h-6 w-6 text-nav-text-color"
              />
            }
          >
            <nav className="flex flex-col">
              <div className="flex items-center justify-center border-b bg-nav-color py-1">
                <MapleLogo />
              </div>
              {session?.user?.member && (
                <Item>
                  <PointsBalance member={session?.user?.member} />
                </Item>
              )}
              {tenant?.stackedNavBarLinks?.map((link, i) =>
                isValidUrl(link?.url) ? (
                  <Item
                    key={`${formatTitleToId(link?.label[locale] || '')}-${i}`}
                  >
                    <a
                      href={link?.url}
                      id={formatTitleToId(link?.label[locale] || '')}
                      target="_blank"
                      className={cn(`text-sm font-semibold`, styling.Link)}
                    >
                      {link?.label[locale]}
                    </a>
                  </Item>
                ) : (
                  <Item
                    key={`${formatTitleToId(link?.label[locale] || '')}-${i}`}
                  >
                    <LocalizedLink
                      href={link?.url}
                      id={formatTitleToId(link?.label[locale] || '')}
                      className={cn(`text-sm font-semibold`, styling.Link)}
                      onClick={() => setIsMainNavOpen(false)}
                    >
                      {link?.label[locale]}
                    </LocalizedLink>
                  </Item>
                ),
              )}
              {tenant?.flags?.earnRewardsPage && (
                <>
                  <Item>
                    <LocalizedLink
                      href="/earn-rewards"
                      id="earn-rewards-link"
                      className={cn('text-sm font-semibold', styling.Link)}
                    >
                      {t('nav.earnRewards')}
                    </LocalizedLink>
                  </Item>
                  <Item>
                    <LocalizedLink
                      href="/travel"
                      id="travel-discounts-link"
                      className={cn('text-sm font-semibold', styling.Link)}
                    >
                      {t('nav.travelAdvisorDiscounts')}
                    </LocalizedLink>
                  </Item>
                </>
              )}
              {tenant?.flags?.bookingMadeEasy && (
                <Item>
                  <LocalizedLink
                    href="/booking-made-easy"
                    id="booking-made-easy-link"
                    className={cn('text-sm font-semibold', styling.Link)}
                  >
                    {t('nav.bookingMadeEasy')}
                  </LocalizedLink>
                </Item>
              )}
              {tenant?.flags?.categories && (
                <Item>
                  <div className="flex w-full items-center justify-between">
                    <button
                      id="nav-bar-shop-categories-button"
                      className={cn('text-sm font-semibold', styling.Link)}
                      onClick={() => setIsShopCategoriesOpen(true)}
                    >
                      {t('nav.shopCategories')}
                    </button>
                    <ChevronRight
                      onClick={() => setIsShopCategoriesOpen(true)}
                      className="h-6 w-6 hover:text-tenant-primary-color"
                    />
                  </div>
                </Item>
              )}
              <BrandedCategoryLinks setIsMainNavOpen={setIsMainNavOpen} />
              {showSpecialOffers && isSpecialOffersEnabled && (
                <Item>
                  <LocalizedLink
                    id="deals-link"
                    href="/special-offers"
                    className={cn('text-sm font-semibold', styling.Link)}
                    onClick={() => setIsMainNavOpen(false)}
                  >
                    {t('nav.specialOffers')}
                  </LocalizedLink>
                </Item>
              )}
              {tenant?.flags?.retailers && isShopAndEarnEnabled && (
                <Item>
                  <LocalizedLink
                    id="shop-and-earn-link"
                    href="/retailers"
                    className={cn(
                      'inline-flex gap-1 text-sm font-semibold',
                      styling.Link,
                    )}
                    onClick={() => setIsMainNavOpen(false)}
                  >
                    {t('nav.shopAndEarn')}
                  </LocalizedLink>
                </Item>
              )}
              {tenant?.flags?.payAnywhere && (
                <Item>
                  <LocalizedLink
                    href="/pay-anywhere"
                    id="pay-anywhere-link"
                    className={cn(
                      'inline-flex gap-1 text-sm font-semibold',
                      styling.Link,
                    )}
                    onClick={() => setIsMainNavOpen(false)}
                  >
                    Pay Anywhere
                  </LocalizedLink>
                </Item>
              )}

              {/* contentful navigation menu */}
              {filterNavLinks(
                directLinks,
                tenant?.theme,
                showPromotionsLink,
              )?.map((directLink) => (
                <Item key={directLink?.id}>
                  <LocalizedLink
                    href={directLink?.to}
                    id={directLink?.id}
                    className={cn(
                      'inline-flex gap-1 text-sm font-semibold',
                      styling.Link,
                    )}
                    onClick={() => setIsMainNavOpen(false)}
                  >
                    {directLink?.name}
                  </LocalizedLink>
                </Item>
              ))}

              {menuLinks?.map((menu, index) => (
                <Item>
                  <div className="flex w-full items-center justify-between">
                    <button
                      id="navigation-menu-button"
                      className={cn('text-sm font-semibold', styling.Link)}
                      onClick={() => setMenuLinkOpen(index + 1)}
                    >
                      {menu?.name}
                    </button>
                    <ChevronRight
                      onClick={() => setMenuLinkOpen(index + 1)}
                      className="h-6 w-6 hover:text-tenant-primary-color"
                    />
                  </div>
                </Item>
              ))}

              <MobileLanguageSwitcher />
              {session?.user?.member ? (
                <>
                  {tenant?.accountUrl ? (
                    <Item>
                      <MobileNavLink
                        id="my-account-link"
                        path={replacePlaceholders(tenant.accountUrl, {
                          locale: locale?.toLowerCase(),
                        })}
                        isClient={false}
                        target="_blank"
                        icon={
                          <UserInitial
                            firstName={session?.user?.member?.firstName}
                          />
                        }
                        translation={t('nav.myAccount')}
                      />
                    </Item>
                  ) : null}
                  {tenant?.flags?.profilePage && (
                    <Item>
                      <MobileNavLink
                        id="profile-link"
                        path="/profile"
                        icon={<User />}
                        translation={t('nav.myProfile')}
                        onClick={() => setIsMainNavOpen(false)}
                      />
                    </Item>
                  )}
                  {tenant?.flags?.wishlist && (
                    <Item>
                      <MobileNavLink
                        disabled={!tenant?.flags?.wishlist}
                        id="wishlist-link"
                        path="/wishlist"
                        icon={
                          <HeartLink
                            className="text-black"
                            savedItemsCount={savedItemIds?.size ?? 0}
                          />
                        }
                        translation={t('nav.myWishList')}
                        onClick={() => setIsMainNavOpen(false)}
                      />
                    </Item>
                  )}
                  <Item>
                    <MobileNavLink
                      id="order-history-link"
                      path="/order-history"
                      icon={<FileText />}
                      translation={t('nav.myOrders')}
                      onClick={() => setIsMainNavOpen(false)}
                    />
                  </Item>
                  {!tenant?.flags?.logoutDisabled && (
                    <Item>
                      <MobileNavLink
                        id="logout-link"
                        path="/auth/sso/logout"
                        icon={<LogIn />}
                        translation={t('nav.logOut')}
                      />
                    </Item>
                  )}
                </>
              ) : (
                <>
                  <Item>
                    <MobileNavLink
                      id="login-link"
                      path={authorizeUrl}
                      isClient={false}
                      icon={<User />}
                      disabled={!authorizeUrl}
                      translation={t('nav.login')}
                      onClick={() => setIsMainNavOpen(false)}
                    />
                  </Item>
                  {tenant?.signupUrl ? (
                    <Item>
                      <MobileNavLink
                        id="signup-link"
                        path={tenant?.signupUrl}
                        icon={<LogIn />}
                        translation={t('nav.signUp')}
                      />
                    </Item>
                  ) : null}
                </>
              )}
            </nav>
          </SheetContent>
          <ShopCategoriesSheet
            isOpen={isShopCategoriesOpen}
            onClose={() => setIsShopCategoriesOpen(false)}
            onSelection={() => setIsMainNavOpen(false)}
            categories={categories}
            isLoading={isLoading}
          />
          {menuLinks?.map((menu, index) => (
            <Sheet
              key={menu?.id}
              open={menuLinkOpen == index + 1}
              onOpenChange={() => setMenuLinkOpen(0)}
            >
              <SheetContent
                side="left"
                className="h-full w-full max-w-none p-0"
                ignoreCloseIcon
              >
                <MobileMenuLink
                  menu={menu as any}
                  onClick={() => {
                    setMenuLinkOpen(0)
                    setIsMainNavOpen(false)
                  }}
                />
              </SheetContent>
            </Sheet>
          ))}
        </Sheet>
      </nav>
      {searchOverlay}
    </header>
  )
}

function ShopCategoriesSheet({
  isOpen,
  onClose,
  onSelection,
  categories,
  isLoading,
}: {
  isOpen: any
  onClose: any
  onSelection: any
  categories: any
  isLoading: any
}) {
  const { locale } = useLocale()
  const { t } = useTranslation()
  const tenant = useTenant()

  if (isLoading) return null
  if (!categories) return null

  const VIEW_ALL_CATEGORY = {
    mainCategory: {
      id: 'view-all-categories',
      name: {
        'en-CA': t('general.viewAll'),
        'fr-CA': t('general.viewAll'),
      },
      viewAll: true,
    },
  }

  let sortedCategories = [...categories]

  // filter out categories with no subcategories
  sortedCategories = sortedCategories.filter((c) =>
    filterShopCategories(c, tenant?.hiddenCategories),
  )

  sortedCategories = [...sortedCategories, VIEW_ALL_CATEGORY]

  const getMainCategoryPath = (name: any) =>
    name ? slugify(name, { lower: true }) : undefined

  const handleLinkClick = () => {
    onSelection()
    onClose()
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="left"
        className="h-full w-full max-w-none p-0"
        ignoreCloseIcon
      >
        <div className="flex flex-row items-center border-b p-4">
          <SheetClose asChild>
            <ArrowLeft className="h-6 w-6" />
          </SheetClose>
          <div
            id="shop-categories"
            className="flex w-full items-center justify-center py-1 text-lg font-semibold"
          >
            {t('nav.shopCategories')}
          </div>
        </div>
        <div className="overflow-y-auto">
          <Accordion type="single" collapsible className="w-full">
            {sortedCategories.map((category) => (
              <AccordionItem
                className="mx-4 px-2 no-underline hover:no-underline"
                key={category.mainCategory.id}
                value={category.mainCategory}
              >
                <AccordionTrigger
                  id={`main-category-${slugify(
                    category?.mainCategory?.name?.[locale]?.toLowerCase() ?? '',
                  )}-accordian`}
                  className="py-3"
                  chevronClassName="hover:text-tenant-primary-color"
                >
                  <LocalizedLink
                    id={`main-category-${slugify(
                      category?.mainCategory?.name?.[locale]?.toLowerCase() ??
                        '',
                    )}-link`}
                    key={category.code}
                    href={`/category/${
                      category?.mainCategory?.viewAll
                        ? 'all'
                        : getMainCategoryPath(
                            category.mainCategory.name[locale],
                          )
                    }`}
                    onClick={handleLinkClick}
                    style={{ textDecoration: 'none' }}
                  >
                    <div className={cn('inline-block text-sm', styling.Link)}>
                      {category.mainCategory.name[locale]}
                    </div>
                  </LocalizedLink>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col space-y-2">
                    {category.mainCategory?.subCategories?.map(
                      (subCategory: any) => (
                        <LocalizedLink
                          key={subCategory.id}
                          id={`sub-category-${
                            subCategory.name[locale]
                              ? slugify(subCategory.name[locale].toLowerCase())
                              : ''
                          }`}
                          href={`/category/${getMainCategoryPath(
                            category.mainCategory.name[locale],
                          )}?subCategory=${subCategory.code}`}
                          onClick={handleLinkClick}
                        >
                          <div
                            className={cn('inline-block text-sm', styling.Link)}
                          >
                            {subCategory.name[locale]}
                          </div>
                        </LocalizedLink>
                      ),
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </SheetContent>
    </Sheet>
  )
}

const MobileNavLink = ({
  id,
  path,
  icon,
  translation,
  isClient = true,
  disabled = false,
  onClick,
  target = '_self',
}: MobileNavLinkPropType) => {
  return (
    <LocalizedLink
      id={id}
      href={path}
      isClient={isClient}
      disabled={disabled}
      className="inline-flex items-center space-x-4 text-sm font-semibold hover:text-tenant-primary-color"
      onClick={onClick}
      target={target}
    >
      {icon}
      <span
        className={cn(styling.Link, 'mt-0.5', {
          'cursor-not-allowed text-gray-400 hover:border-none hover:text-gray-400':
            disabled,
        })}
      >
        {translation}
      </span>
    </LocalizedLink>
  )
}

const MobileLanguageSwitcher = () => {
  const isClient = useIsClient()
  const { currentLanguage, availableLanguages, changeLocale } =
    useLocaleChange()

  // skip rendering on the server to avoid hydration issues
  // since the locale is not available at that time
  if (!isClient || availableLanguages?.length <= 1) return null

  return (
    <Item>
      <div id="language-switcher" className="flex space-x-4">
        {availableLanguages.map((lang) => (
          <button
            id={`language-switcher-${lang.key}`}
            key={lang.key}
            className={cn(
              'cursor-pointer text-sm font-semibold hover:text-tenant-primary-color',
              currentLanguage === lang.key && 'text-tenant-primary-color',
            )}
            onClick={() => changeLocale(lang.key)}
          >
            {lang.value}
            <span className="sr-only">
              {currentLanguage === lang.key ? 'selected' : 'unselected'}
            </span>
          </button>
        ))}
      </div>
    </Item>
  )
}
